import { Badge, Tooltip, type MantineColor } from '@mantine/core';
import type { Device } from '@motionsafe/ms-console-api/admin/v1/device_pb.js';
import { DeviceStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IconDeviceTabletCheck, IconDeviceTabletExclamation, IconDeviceTabletOff, IconDeviceTabletPause, IconDeviceTabletPlus, IconDeviceTabletQuestion, IconDeviceTabletX, type Icon } from '@tabler/icons-react';

import { when } from '../lib/format';
import { ICON_INLINE } from '../theme';

// State words are the audit trail's words, verbatim. Field Green for active,
// Workshop Amber for unsupported and unassigned, Vermillion for what has
// already happened to a tablet: suspended, revoked.
export const statusColor = (s: DeviceStatus): MantineColor =>
  s === DeviceStatus.ACTIVE ? 'success' : s === DeviceStatus.UNSUPPORTED || s === DeviceStatus.OUT_OF_CIRCULATION ? 'warning' : s === DeviceStatus.SUSPENDED || s === DeviceStatus.REVOKED ? 'danger' : 'gray';
export const statusWord = (s: DeviceStatus) => DeviceStatus[s].toLowerCase().replace(/_/g, ' ');

const ICON: Record<DeviceStatus, Icon> = {
  [DeviceStatus.UNSPECIFIED]: IconDeviceTabletQuestion,
  [DeviceStatus.ACTIVE]: IconDeviceTabletCheck,
  [DeviceStatus.SUSPENDED]: IconDeviceTabletPause,
  [DeviceStatus.UNSUPPORTED]: IconDeviceTabletExclamation,
  [DeviceStatus.REVOKED]: IconDeviceTabletX,
  [DeviceStatus.OUT_OF_CIRCULATION]: IconDeviceTabletOff,
};

// What the state means for the people who sign in on it.
const MEANING: Record<DeviceStatus, string> = {
  [DeviceStatus.UNSPECIFIED]: '',
  [DeviceStatus.ACTIVE]: 'Operators can sign in on it.',
  [DeviceStatus.SUSPENDED]: 'Sign-in refused until it is reinstated.',
  [DeviceStatus.UNSUPPORTED]: 'Marked by MotionSafe staff. Sign-in refused.',
  [DeviceStatus.REVOKED]: 'Permanent. Sign-in refused.',
  [DeviceStatus.OUT_OF_CIRCULATION]: 'Belongs to no organization, and clears what it holds at its next contact.',
};

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const mark = { display: 'inline-flex', verticalAlign: 'middle' } as const;

// A row's status in one mark: a quiet check for an active device, since
// that is the ordinary case, and the state's icon on its tint for anything
// else. The word, when it changed, and the note left with it are on hover;
// the word is what a screen reader hears.
export function StatusMark({ d }: { d: Device }) {
  // A registration nobody has assigned yet: Workshop Amber, as unassigned is (DESIGN.web.md 7.6).
  const fresh = !d.orgId && d.status !== DeviceStatus.OUT_OF_CIRCULATION;
  const word = fresh ? 'new' : statusWord(d.status);
  const since = !fresh && d.status !== DeviceStatus.ACTIVE && d.statusChangedAt ? `, ${when(d.statusChangedAt, { relative: true })}` : '';
  const meaning = fresh ? 'Registered, and waiting to be assigned to an organization.' : MEANING[d.status];
  const label = (
    <>
      <div>{`${capital(word)}${since}. ${meaning}`}</div>
      {d.statusNote && <div>{`Note: ${d.statusNote}`}</div>}
    </>
  );
  const Mark = fresh ? IconDeviceTabletPlus : ICON[d.status];
  if (!fresh && d.status === DeviceStatus.ACTIVE) {
    return (
      <Tooltip label={label} multiline maw={260}>
        <span style={mark}><Mark {...ICON_INLINE} color="var(--mantine-color-success-text)" role="img" aria-label={word} /></span>
      </Tooltip>
    );
  }
  return (
    <Tooltip label={label} multiline maw={260}>
      <Badge component="span" px={6} color={fresh ? 'warning' : statusColor(d.status)} role="img" aria-label={word}>
        <Mark size={16} stroke={1.75} aria-hidden style={{ display: 'block' }} />
      </Badge>
    </Tooltip>
  );
}
